/**
 * NH Back to Top - 返回顶部按钮组件
 *
 * 功能：
 * - 滚动超过阈值时显示按钮
 * - 点击按钮平滑滚动到页面顶部
 * - 支持键盘导航和无障碍访问
 * - 尊重用户的减少动画偏好
 */

class NhBackToTop extends HTMLElement {
  constructor() {
    super();
    this.threshold = 300;
    this.isVisible = false;
    this.rafId = null;
    this.button = null;

    // 绑定方法
    this.handleScroll = this.handleScroll.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // 获取按钮元素
    this.button = this.querySelector('.nh-btt__button');

    if (!this.button) {
      console.warn('NH Back to Top: Button element not found');
      return;
    }

    // 解析阈值
    this.threshold = parseInt(this.dataset.threshold || '300', 10);

    // 绑定事件
    this.bindEvents();

    // 初始检查
    this.checkVisibility();
  }

  disconnectedCallback() {
    this.unbindEvents();

    // 取消待处理的 RAF
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  bindEvents() {
    // 使用 passive 监听器优化滚动性能
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // 点击事件
    this.button.addEventListener('click', this.handleClick);

    // 键盘导航支持
    this.addEventListener('keydown', this.handleKeydown);
  }

  unbindEvents() {
    window.removeEventListener('scroll', this.handleScroll);
    this.button?.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  /**
   * 滚动事件处理（使用 RAF 节流）
   */
  handleScroll() {
    // 如果已有待处理的 RAF，跳过
    if (this.rafId) return;

    this.rafId = requestAnimationFrame(() => {
      this.checkVisibility();
      this.rafId = null;
    });
  }

  /**
   * 检查并更新可见性状态
   */
  checkVisibility() {
    const scrollY = window.scrollY || window.pageYOffset;
    const shouldShow = scrollY > this.threshold;

    // 只在状态变化时更新
    if (shouldShow !== this.isVisible) {
      this.isVisible = shouldShow;

      // 更新 data-visible 属性触发 CSS 动画
      if (shouldShow) {
        this.setAttribute('data-visible', '');
      } else {
        this.removeAttribute('data-visible');
      }

      // 更新无障碍属性
      if (this.button) {
        this.button.setAttribute('tabindex', shouldShow ? '0' : '-1');
        this.button.setAttribute('aria-hidden', String(!shouldShow));
      }
    }
  }

  /**
   * 点击事件处理
   */
  handleClick(event) {
    event.preventDefault();
    this.scrollToTop();
  }

  /**
   * 键盘事件处理
   */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToTop();
    }
  }

  /**
   * 滚动到页面顶部
   */
  scrollToTop() {
    // 检查用户是否偏好减少动画
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    });

    // 滚动完成后聚焦到页面主内容区域
    setTimeout(() => {
      const mainContent = document.getElementById('MainContent') || document.querySelector('main');
      if (mainContent) {
        mainContent.focus({ preventScroll: true });
      }
    }, 100);
  }
}

// 注册自定义元素
if (!customElements.get('nh-back-to-top')) {
  customElements.define('nh-back-to-top', NhBackToTop);
}
